// src/components/AdminPage.js
import React, { useEffect, useState } from 'react';
import { Button, Typography, Container, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getPendingLaps, getAllLaps, approveLap, denyLap, getLeaderboard, getPointsStructure, savePointsStructure, deleteLap, getVehicleModels, addVehicleModel, deleteVehicleModel, getCircuits } from '../utils/firestoreUtils';
import { Link } from 'react-router-dom';
import NewsEditor from './NewsEditor';
import { useAdminNotifications } from '../utils/AdminNotificationContext';

function AdminPage() {
  const { refreshPendingLaps } = useAdminNotifications();
  const [allLaps, setAllLaps] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [year, setYear] = useState(2026);
  const [pointsTable, setPointsTable] = useState(Array(15).fill().map(() => Array(15).fill(0)));
  const [isLocked, setIsLocked] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [processingLapId, setProcessingLapId] = useState(null);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [newModelName, setNewModelName] = useState('');

  const fetchData = async () => {
    const laps = await getAllLaps();
    const circuits = await getCircuits();
    const processedLaps = await Promise.all(laps.map(async (lap) => {
      const [circuitId, layoutName] = lap.track.split('-');
      const circuit = circuits.find(c => c.id === circuitId);
      const trackName = circuit ? `${circuit.name} - ${layoutName}` : lap.track;
      return { ...lap, trackName };
    }));
    setAllLaps(processedLaps);
    const lb = await getLeaderboard();
    setLeaderboard(lb);
    const models = await getVehicleModels();
    setVehicleModels(models);
  };

  useEffect(() => { fetchData(); }, []);

  const fetchPointsStructure = async () => {
    const data = await getPointsStructure(year);
    if (data) {
      const table = data.pointsTable.map(row => row.slice(0, 15));
      while (table.length < 15) table.push(Array(15).fill(0));
      setPointsTable(table);
      setIsLocked(data.isLocked || false);
    } else {
      setPointsTable(Array(15).fill().map(() => Array(15).fill(0)));
      setIsLocked(false);
    }
  };

  useEffect(() => { fetchPointsStructure(); }, [year]);

  const handleApprove = async (lapId, userId) => {
    setProcessingLapId(lapId);
    await approveLap(lapId, userId);
    await fetchData();
    await refreshPendingLaps();
    setProcessingLapId(null);
  };

  const handleDeny = async (lapId, userId) => {
    setProcessingLapId(lapId);
    await denyLap(lapId, userId);
    await fetchData();
    await refreshPendingLaps();
    setProcessingLapId(null);
  };

  const handleUnapprove = async (lapId, userId) => {
    setProcessingLapId(lapId);
    await denyLap(lapId, userId);
    await fetchData();
    await refreshPendingLaps();
    setProcessingLapId(null);
  };

  const handleDelete = async (lapId, userId) => {
    setProcessingLapId(lapId);
    await deleteLap(userId, lapId);
    await fetchData();
    await refreshPendingLaps();
    setProcessingLapId(null);
  };

  const handleAddVehicleModel = async () => {
    if (newModelName.trim()) {
      await addVehicleModel(newModelName.trim());
      setNewModelName('');
      await fetchData();
    }
  };

  const handleDeleteVehicleModel = async (modelId) => {
    await deleteVehicleModel(modelId);
    await fetchData();
  };

  const handleSave = async () => {
    await savePointsStructure(year, pointsTable, true);
    setIsLocked(true);
  };

  const pendingCount = allLaps.filter(l => !l.hasBeenReviewed).length;

  const formatLapTime = (time) => {
    if (!time) return '—';
    const s = parseFloat(time);
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.round((s % 1) * 1000);
    if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      <div className="page-header">
        <h1 className="page-title">Admin</h1>
        <p className="page-subtitle">Manage laps, points and configuration</p>
      </div>

      {/* ── SEASON LAPS ── */}
      <div className="admin-section">
        <div className="admin-section-header">
          <span className="admin-section-title">Season Laps</span>
          {pendingCount > 0 && <span className="admin-section-badge">{pendingCount} pending</span>}
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Event</th>
                <th>Circuit</th>
                <th>Date</th>
                <th>Lap Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allLaps.map((lap) => {
                const status = lap.isApproved ? 'approved' : lap.hasBeenReviewed ? 'denied' : 'pending';
                const statusLabel = lap.isApproved ? 'Approved' : lap.hasBeenReviewed ? 'Denied' : 'Pending';
                const isProcessing = processingLapId === lap.id;
                return (
                  <tr key={lap.id}>
                    <td style={{ fontWeight: 600 }}>{lap.userName}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{lap.eventName}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{lap.trackName}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {new Date(lap.date).toLocaleDateString()}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{formatLapTime(lap.bestLap)}</td>
                    <td><span className={`status-chip ${status}`}>{statusLabel}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {lap.proof && (
                        <IconButton onClick={() => window.open(lap.proof, '_blank')} size="small" title="View proof" sx={{ mr: 0.5 }}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                      {isProcessing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : !lap.hasBeenReviewed ? (
                        <>
                          <button className="admin-btn-approve" onClick={() => handleApprove(lap.id, lap.userId)}>Approve</button>
                          <button className="admin-btn-deny" onClick={() => handleDeny(lap.id, lap.userId)}>Deny</button>
                        </>
                      ) : lap.isApproved ? (
                        <button className="admin-btn-warn" onClick={() => handleUnapprove(lap.id, lap.userId)}>Unapprove</button>
                      ) : (
                        <button className="admin-btn-deny" onClick={() => handleDelete(lap.id, lap.userId)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {allLaps.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '32px 0' }}>No laps yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── USERS & POINTS ── */}
      <div className="admin-section">
        <div className="admin-section-header">
          <span className="admin-section-title">Users and Points</span>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>User</th><th style={{ textAlign: 'right' }}>Points</th></tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, i) => (
                <tr key={entry.userId}>
                  <td style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: i === 0 ? 'var(--gold-subtle)' : i === 1 ? 'rgba(138,155,174,.12)' : 'var(--surface-2)',
                      color: i === 0 ? 'var(--gold)' : i === 1 ? 'var(--silver)' : 'var(--text-tertiary)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                    }}>{i + 1}</span>
                    {entry.name}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`points-badge ${entry.points === 0 ? 'zero' : ''}`}>{entry.points}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── POINTS STRUCTURE ── */}
      <div className="admin-section">
        <div className="admin-section-header">
          <span className="admin-section-title">Points Structure</span>
        </div>
        <div style={{ padding: '20px 20px 0' }}>
          <TextField label="Year" type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))}
            disabled={isLocked} size="small" sx={{ mb: 2, width: 100 }} />
          <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: 12 }}>
            Rows = finishing position (1–15). Columns = number of competitors (1–15).
          </p>
        </div>
        <div className="admin-table-wrap" style={{ padding: '0 20px 20px' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Pos</th>
                {Array.from({ length: 15 }, (_, i) => (
                  <th key={i} style={{ padding: '6px 4px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.72rem', fontWeight: 600 }}>{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pointsTable.map((row, pos) => (
                <tr key={pos}>
                  <td style={{ padding: '3px 8px', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{pos + 1}</td>
                  {row.slice(0, 15).map((points, comp) => (
                    <td key={comp} style={{ padding: '3px 2px' }}>
                      <input
                        type="number"
                        value={points}
                        onChange={(e) => {
                          const newTable = [...pointsTable];
                          newTable[pos] = [...newTable[pos]];
                          newTable[pos][comp] = parseInt(e.target.value) || 0;
                          setPointsTable(newTable);
                        }}
                        disabled={isLocked}
                        className="points-grid-input"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10 }}>
          <button className="admin-btn-approve" style={{ padding: '8px 20px', fontSize: '0.85rem' }} onClick={handleSave} disabled={isLocked}>
            Save & Lock
          </button>
          {isLocked && (
            <button className="admin-btn-warn" style={{ padding: '8px 20px', fontSize: '0.85rem' }} onClick={() => setUnlockDialogOpen(true)}>
              Unlock
            </button>
          )}
        </div>
      </div>

      {/* ── VEHICLE MODELS ── */}
      <div className="admin-section">
        <div className="admin-section-header">
          <span className="admin-section-title">Vehicle Models</span>
        </div>
        <div style={{ padding: 20 }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField label="New Model" value={newModelName} onChange={(e) => setNewModelName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddVehicleModel()}
              size="small" sx={{ width: 200 }} />
            <Button onClick={handleAddVehicleModel} variant="contained" size="small" sx={{ height: 40, textTransform: 'none' }}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {vehicleModels.map((model) => (
              <div key={model.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-pill)', padding: '4px 12px',
                fontSize: '0.82rem', color: 'var(--text-primary)',
              }}>
                {model.name}
                <button
                  onClick={() => handleDeleteVehicleModel(model.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0, display: 'flex', alignItems: 'center', borderRadius: '50%' }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            ))}
          </Box>
        </div>
      </div>

      <NewsEditor />

      {/* ── CIRCUIT MANAGER ── */}
      <div className="admin-section">
        <div className="admin-section-header">
          <span className="admin-section-title">Circuit Manager</span>
        </div>
        <div style={{ padding: 20 }}>
          <Link to="/circuit-manager">
            <Button variant="contained" sx={{ textTransform: 'none' }}>Open Circuit Manager</Button>
          </Link>
        </div>
      </div>

      <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)}>
        <DialogTitle>⚠️ Unlock Points Structure</DialogTitle>
        <DialogContent>Editing this will recalculate actual user points. Don't change it unless you know what you're doing!</DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { setIsLocked(false); setUnlockDialogOpen(false); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Unlock</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminPage;
